/* eslint-disable import/no-anonymous-default-export */

/**
 * database configuration
 * ----------------------------------------
 * Format matches feature config modules so it is auto-collected by your existing
 * Webpack loader (require.context) alongside other feature configs.
 *
 * Version semantics (store.object.data): "MAJOR.MINOR.PATCH"
 * - MAJOR (store): Dexie store definition change → requires DB version bump.
 * - MINOR (object): object model changes → coerce existing records.
 * - PATCH (data): data-only adjustments (upserts) → no schema change.
 */
const VERSION = "1.1.1";

/**
 * modelStore Configuration Object
 *
 * Represents a metadata record for tracking store configuration state.
 * This object serves as a special meta-row that stores versioning and
 * descriptive information about the store itself.
 *
 * @typedef {Object} ModelStore
 *
 * @property {string} id
 *   Unique identifier for the store's metadata row.
 *   MUST be set to "__schema" for the store's own meta row.
 *   This special ID distinguishes the meta record from regular data entries.
 *
 * @property {string} version
 *   The latest applied semantic version for this store.
 *   Format: "MAJOR.MINOR.PATCH" (e.g., "1.2.3")
 *
 * @property {number} created
 *   Unix timestamp (milliseconds) of when this record was created.
 *   Defaults to Date.now() at initialization.
 *
 * @property {number} modified
 *   Unix timestamp (milliseconds) of the last modification.
 *   Should be updated via Date.now() whenever the record changes.
 *
 * @example
 * export const modelStore = {
 *   id: "__history",
 *   version: "1.1.1",
 *   created: 1699564800000,
 *   modified: 1699564800000,
 * };
 */
export const modelStore = {
  id: "", // MUST be "__schema" for the store's own meta row
  version: "", // latest applied semantic version for that store
  created: Date.now(),
  modified: Date.now(),
};

/**
 * modelData Configuration Object
 *
 * Represents a single store version. Each instance tracks one store change
 *
 * @typedef {Object} ModelData
 *
 * @property {string} id
 *   Unique identifier for the store's data row.
 *   ID is set from the store name
 *
 * @property {string} version
 *   The latest applied semantic version for this store.
 *   Format: "MAJOR.MINOR.PATCH" (e.g., "1.2.3")
 *
 * @property {number} created
 *   Unix timestamp (milliseconds) of when this record was created.
 *   Defaults to Date.now() at initialization.
 *
 * @property {number} modified
 *   Unix timestamp (milliseconds) of the last modification.
 *   Should be updated via Date.now() whenever the record changes.
 *
 * @example
 * export const modelStore = {
 *   id: "__history",
 *   version: "1.1.1",
 *   created: 1699564800000,
 *   modified: 1699564800000,
 * };
 */
export const modelData = {
  id: "", // store name, e.g., "settings", "todo", etc.
  version: "", // latest applied semantic version for that store
  created: Date.now(),
  modified: Date.now(),
};

export default {
  /**
   * version  MAJOR.MINOR.PATCH
   *
   * For new store, set version at 1.1.1
   *
   * To apply patch data object key values, increment position 3 (1.1.2, so 1.1.1 -> 1.1.2)
   * To apply minor "model" updates, increment position 2 (1.2.1, so 1.1.1 || 1.1.2 || 1.1.6 -> 1.2.1)
   * To apply MAJOR "store" updates, increment postion 1 (2.1.1, so 1.2.1 || 1.2.7 || 1.9.4 -> 2.1.1)
   *
   * Versions cannot jump sequence
   *   (e.g. ver 1.1.1 cannot jump to 1.1.5, ver 1.1.2 cannot jump to 1.2.2, it must go to 1.2.1)
   *
   * @property {string} version - REQUIRED Version used in update and must match version in file name
   */
  version: VERSION,

  /**
   * forceUpdate
   *
   * to be used when updates are required not optional by user
   *
   * true  this will skip the user prompt
   * false this will allow user to skip update
   *
   * ** FUTURE behavior will be to prompt user to backup first and then force the update
   *
   * @property {boolean} forceUpdate - force update without allowing user to skip
   */
  forceUpdate: true,

  /**
   * type
   *
   * Type options (MAJOR || MINOR || PATCH)
   *
   * This value is for convenience and quick reference and is not used to determine any update decisions in code
   *
   * @property {string} type - Human-readable indication representing the version number update type MAJOR.MINOR.PATCH
   * */
  type: "MAJOR", // major because it is the initial version

  /**
   * changeLog
   *
   * Intended for documentation purposes but could be used for other purposes as well
   *
   * @property {string[]} changeLog - Array of specific changes this config contains
   */
  changeLog: [
    "- create store and indexes",
    "- establish store and data models",
  ],

  /**
   * changeDescription
   *
   * Description of changes in this config update. Intended to be informative to app user
   *  if prompted to update
   *
   * @property {string} changeDescription - Public description of update
   */
  changeDescription: "Initiate system store.",

  /**
   * stores (MAJOR)
   *
   * ONLY supports a single store defined per config, new store = new config file
   *  additional configured stores will be ignored
   *
   * Store modifications require a database version bump
   *
   *  + to modify indexes (adding), add index configuration, create a MAJOR update config file
   *  - to modify indexes (removing), remove index, leaving other indexes in place, crate MAJOR update config file
   *
   * @constant {Object} stores Accepts only one store and schema definition
   */
  stores: {
    __schema: "id", // meta: per-store version row keyed by store name
  },

  /**
   * Models (MINOR)
   *
   * Minor updates will make configured change to ALL data objects
   * For storeObject
   *  + to modify model (adding keys), add key and create a MINOR update config file
   *  - to modify model (removing keys), remove key leaving other keys
   *
   * For dataObject
   *  + to modify model (adding keys), add key and create a MINOR update config file
   *  - to modify model (removing keys), remove key leaving other keys and configure
   *    transform hook to run keyPruning. Keys are not removed automatically
   *
   * @constant {Object} storeObject Defines model for single store object
   * @constant {Object} dataObject Defines model for all data objects
   */
  model: {
    storeObject: modelStore,
    dataObject: modelData,
  },

  /**
   * data (PATCH)
   *
   * When store is initiated (v1.1.1)
   * - Seed the storeObject row in data for this store if you want to lock their starting versions.
   * - Fallback will create an object using model and will only apply version and date/time stamps
   *
   * @property {Array.<{...dataObject}>} data define data items to add to store for this patch update
   *    if no data needs to be added, keep array empty
   */
  data: [
    // __schema is unique in that it tracks all stores even itself.
    //    so even if we added a store object, it would be overwritten
    //    since data rows tracking stores use the store name as id
    // {
    //   id: "__schema",
    //   label: "Schema Meta",
    //   description: "Tracks per-store schema/object/data versions.",
    //   version: VERSION,
    //   created: Date.now(),
    //   modified: Date.now(),
    // },
  ],

  /**
   * Hooks
   *
   * - hooks are for data objects and do not apply to storeObjects
   */
  hooks: {
    // Will run for *data rows only* (never for the storeObject/meta row)
    // transform: (obj, ctx) => {
    //   console.debug(
    //     `[settings@${ctx.stepVersion}] transform`,
    //     Object.keys(obj)
    //   );
    //   // Only prune in *this* version, e.g. when you removed fields
    //   ctx.utils.keyPruning(obj, ctx.model);
    //   return obj;
    // },
    // beforePopulate: async ({ db, store, version }) => {},
    // afterPopulate: async ({ db, store, version }) => {},
  },
};
/* eslint-enable import/no-anonymous-default-export */
